import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Typography,
  Stack,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  InputAdornment
} from '@mui/material';
import * as Icons from "../constants/icons";
import { ProjectFilters, SortConfig, DEFAULT_FILTERS } from '../hooks/useProjectTable';
import { CATEGORY_LARGE, CATEGORY_MEDIUM_BY_LARGE, PHASE } from '../constants/masterData';
import { sfaSavedFilterApi, SavedFilterScope } from '../hooks/useSfaSavedFilters';
import { useAuth } from '../firebase/AuthProvider';
import { useMasterData } from '../providers/MasterDataProvider';
import { useNotification } from '../providers/NotificationProvider';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_FAB_CTA_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import SfaListColumnsEditor from './sfa/SfaListColumnsEditor';
import { DEFAULT_LIST_COLUMNS } from './sfa/sfaListColumns';

interface SalesProjectSearchFiltersProps {
  filters: ProjectFilters;
  onSearch: (newFilters: ProjectFilters) => void;
  sort: SortConfig; // Added sort prop
  onSortChange: (newSort: SortConfig) => void; // Added onSortChange prop
  viewLevel?: string;
  onSaveSuccess?: () => void;
  showSearchButton?: boolean;
  showResetButton?: boolean;
  savedFilterId?: number;            // 既存の条件を更新するときの ID（案件条件リストの編集。作成者のみ）
  initialFilterName?: string;
  initialScope?: SavedFilterScope;   // 編集時の共有区分
  initialSharedWith?: string[];      // 編集時の共有メンバー（メールアドレス）
  phaseOptions?: readonly string[]; // フェーズの選択肢を差し替える（統合版一覧・案件条件リストは統合版のフェーズ）
  onReset?: () => void; // リセットを押したときの追加処理（統合版一覧は一覧の先頭から表示する）
  sortOptions?: { value: string; label: string }[]; // 並び替えの選択肢を差し替える（統合版は列に合わせた項目）
  // 表示列（統合版）。columns を渡すと 3 つ目のタブ「表示列」が出る。検索の実行で onColumnsChange に渡し、条件の保存にも含める
  columns?: string[];
  onColumnsChange?: (cols: string[]) => void;
}

// 旧「案件リスト」の並び替えの選択肢
const LEGACY_SORT_OPTIONS = [
  { value: 'updated_date', label: '最終更新日' },
  { value: 'project_name', label: '案件名' },
  { value: 'phase', label: 'フェーズ' },
  { value: 'client_name', label: '取引先' },
  { value: 'main_staff_name', label: 'メイン担当者' },
  { value: 'estimated_total_construction_cost', label: '想定工事費' },
];

const SalesProjectSearchFilters: React.FC<SalesProjectSearchFiltersProps> = ({
  filters,
  onSearch,
  sort, // Destructure sort
  onSortChange, // Destructure onSortChange
  viewLevel = 'individual',
  onSaveSuccess,
  showSearchButton = true,
  showResetButton = true,
  savedFilterId,
  initialFilterName = '',
  initialScope = 'personal',
  initialSharedWith,
  phaseOptions,
  onReset,
  sortOptions = LEGACY_SORT_OPTIONS,
  columns,
  onColumnsChange,
}) => {
  const { staffList, branchList, departmentList } = useMasterData();
  const { user } = useAuth();
  const notify = useNotification();
  const [activeTab, setActiveTab] = useState(0); // Added activeTab state
  const [localFilters, setLocalFilters] = useState<ProjectFilters>(filters);
  const [localSort, setLocalSort] = useState<SortConfig>(sort); // Added localSort state

  // 親コンポーネントからの変更を同期（保存済み条件の適用などのため）
  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  React.useEffect(() => {
    setLocalSort(sort);
  }, [sort]);

  // 表示列（統合版だけ）。検索の実行で親へ渡す
  const [localColumns, setLocalColumns] = useState<string[]>(columns ?? DEFAULT_LIST_COLUMNS);
  React.useEffect(() => {
    if (columns) setLocalColumns(columns);
  }, [columns]);

  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [filterName, setFilterName] = useState(initialFilterName);
  const [isSaving, setIsSaving] = useState(false);

  // 共有区分と共有メンバー（作成者本人は選択肢に出さず、サーバー側で必ず含める）
  const myEmail = (user?.email || '').toLowerCase();
  const otherMembers = (emails?: string[]) => (emails ?? []).filter((e) => e.toLowerCase() !== myEmail);
  const [scope, setScope] = useState<SavedFilterScope>(initialScope);
  const [sharedWith, setSharedWith] = useState<string[]>(otherMembers(initialSharedWith));
  const staffOptions = React.useMemo(
    () => (staffList as { staff_id?: string; staff_name?: string }[])
      .filter((s) => s.staff_id && s.staff_id.toLowerCase() !== myEmail)
      .map((s) => ({ email: s.staff_id as string, name: s.staff_name || (s.staff_id as string) })),
    [staffList, myEmail],
  );
  const staffByEmail = React.useMemo(() => new Map(staffOptions.map((o) => [o.email, o])), [staffOptions]);

  // --------------------------------------------------
  // 📥 保存ダイアログ
  // --------------------------------------------------
  // 開くたびに編集元の値（新規なら空・個人）へ戻す
  const resetSaveDialog = () => {
    setFilterName(initialFilterName || '');
    setScope(initialScope);
    setSharedWith(otherMembers(initialSharedWith));
  };
  const handleOpenSaveDialog = () => {
    resetSaveDialog();
    setIsSaveDialogOpen(true);
  };

  // 入力をクリアする
  const handleCloseSaveDialog = () => {
    setIsSaveDialogOpen(false);
    resetSaveDialog();
  };

  // --------------------------------------------------
  // 📥 入力変更時の処理
  // --------------------------------------------------
  const handleChange = (field: string, value: any) => {
    setLocalFilters(prev => {
      const next = { ...prev, [field]: value };
      // 大項目変更時、対応しない中項目はクリアする
      if (field === 'category_large') {
        const validMediums = value ? CATEGORY_MEDIUM_BY_LARGE[value] ?? [] : [];
        if (prev.category_medium && !validMediums.includes(prev.category_medium)) {
          next.category_medium = '';
        }
      }
      return next;
    });
  };

  // 選択済み大項目に対応する中項目の選択肢を取得
  const getMediumOptions = (large: string): readonly string[] => {
    if (!large) return [];
    return CATEGORY_MEDIUM_BY_LARGE[large] ?? [];
  };

  // ソート変更時の処理
  const handleSortFieldChange = (field: string) => {
    const newSort = { ...localSort, field };
    setLocalSort(newSort);
  };

  const handleSortOrderChange = (event: React.MouseEvent<HTMLElement>, order: 'asc' | 'desc') => {
    if (order !== null) {
      const newSort = { ...localSort, order };
      setLocalSort(newSort);
    }
  };

  // 検索実行
  const handleSearch = () => {
    onSearch(localFilters);
    onSortChange(localSort); // Call onSortChange
    if (columns) onColumnsChange?.(localColumns);
  };

  // フィルタのリセット（表示列も既定に戻す）
  const handleReset = () => {
    const defaultSort: SortConfig = { field: 'phase', order: 'asc' };
    setLocalFilters(DEFAULT_FILTERS);
    setLocalSort(defaultSort); // Reset localSort
    onSearch(DEFAULT_FILTERS);
    onSortChange(defaultSort); // Call onSortChange with default
    if (columns) { setLocalColumns([...DEFAULT_LIST_COLUMNS]); onColumnsChange?.([...DEFAULT_LIST_COLUMNS]); }
    onReset?.();
  };

  // 条件の保存処理
  const handleSaveFilter = async () => {
    if (!user?.email || !filterName.trim()) return;
    if (scope === 'shared' && sharedWith.length === 0) {
      notify.warning('共有するメンバーを選んでください。');
      return;
    }

    setIsSaving(true);
    try {
      // Cloud SQL（trn_sfa_saved_filters）へ保存。id があれば更新（作成者のみ。他人は 403 で message が返る）
      await sfaSavedFilterApi.save(user.email, {
        ...(savedFilterId ? { id: savedFilterId } : {}),
        name: filterName.trim(),
        filters: localFilters,
        sort: localSort,
        ...(columns ? { columns: localColumns } : {}),
        scope,
        shared_with: scope === 'shared' ? sharedWith : [],
      });
      notify.success(savedFilterId ? '検索条件を更新しました。' : '検索条件を保存しました。');
      setIsSaveDialogOpen(false);
      if (!savedFilterId) setFilterName('');
      if (onSaveSuccess) onSaveSuccess();
    } catch (error: any) {
      console.error("Error saving filter:", error);
      notify.error(error?.message || '保存に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 200px)',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: '#f8fafc',
            minHeight: '32px',
            flexShrink: 0,
            '& .MuiTab-root': { fontSize: '0.75rem', minHeight: '32px' }
          }}
        >
          <Tab icon={<Icons.FilterAlt sx={{ fontSize: 18 }} />} iconPosition="start" label="抽出条件" />
          <Tab icon={<Icons.Sort sx={{ fontSize: 18 }} />} iconPosition="start" label="並び替え" />
          {columns && <Tab icon={<Icons.ViewColumn sx={{ fontSize: 18 }} />} iconPosition="start" label="表示列" />}
        </Tabs>

        <Box sx={{ p: 2, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          {activeTab === 0 ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="案件キーワード"
                  placeholder="案件名・案件番号から検索"
                  value={localFilters.project_name || ''}
                  onChange={(e) => handleChange('project_name', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '13px' },
                    '& .MuiOutlinedInput-input': { fontSize: '13px' },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  size="small"
                  options={[...(phaseOptions ?? PHASE)]}
                  value={localFilters.phase || []}
                  onChange={(event, newValue) => {
                    handleChange('phase', newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="フェーズ"
                      placeholder="フェーズを選択（複数可）"
                      sx={{
                        '& .MuiInputLabel-root': { fontSize: '13px' },
                        '& .MuiOutlinedInput-input': { fontSize: '13px' },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'background.paper',
                        }
                      }}
                    />
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={option}
                          {...tagProps}
                          size="small"
                          sx={{ fontSize: '11px', height: '20px' }}
                        />
                      );
                    })
                  }
                  sx={{ width: '80%' }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  案件種別（複数可・未選択ですべて）
                </Typography>
                <ToggleButtonGroup
                  color="primary"
                  size="small"
                  value={localFilters.project_type || []}
                  onChange={(event, newValue: string[]) => handleChange('project_type', newValue)}
                  sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 2, fontSize: '13px' } }}
                >
                  <ToggleButton value="purchase">仕入</ToggleButton>
                  <ToggleButton value="sales">売上</ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              {(viewLevel === 'all' || viewLevel === 'branch') && (
                <>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={branchList.map((option) => option.branch_name || '')}
                      value={localFilters.main_staff_branch_name || []}
                      onChange={(event, newValue) => {
                        handleChange('main_staff_branch_name', newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="支店名"
                          placeholder="支店を選択（複数可）"
                          fullWidth
                          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                          sx={{
                            '& .MuiInputLabel-root': { fontSize: '13px' },
                            '& .MuiOutlinedInput-input': { fontSize: '13px' },
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'background.paper',
                            }
                          }}
                        />
                      )}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              label={option}
                              {...tagProps}
                              size="small"
                              sx={{ fontSize: '11px', height: '20px' }}
                            />
                          );
                        })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={departmentList.map((option) => option.department_name || '')}
                      value={localFilters.main_staff_department_name || []}
                      onChange={(event, newValue) => {
                        handleChange('main_staff_department_name', newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="部署名"
                          placeholder="部署を選択（複数可）"
                          fullWidth
                          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                          sx={{
                            '& .MuiInputLabel-root': { fontSize: '13px' },
                            '& .MuiOutlinedInput-input': { fontSize: '13px' },
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'background.paper',
                            }
                          }}
                        />
                      )}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              label={option}
                              {...tagProps}
                              size="small"
                              sx={{ fontSize: '11px', height: '20px' }}
                            />
                          );
                        })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={staffList.map((option) => option.staff_name || '')}
                      value={localFilters.main_staff_name || ''}
                      onInputChange={(event, newInputValue) => {
                        handleChange('main_staff_name', newInputValue);
                      }}
                      onChange={(event, newValue) => {
                        handleChange('main_staff_name', newValue || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="メイン担当者"
                          fullWidth
                          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                          sx={{
                            '& .MuiInputLabel-root': { fontSize: '13px' },
                            '& .MuiOutlinedInput-input': { fontSize: '13px' },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="大項目"
                  value={localFilters.category_large || ''}
                  onChange={(e) => handleChange('category_large', e.target.value)}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '13px' },
                    '& .MuiOutlinedInput-input': { fontSize: '13px' },
                  }}
                >
                  <MenuItem value="">すべて</MenuItem>
                  {CATEGORY_LARGE.map((val) => (
                    <MenuItem key={val} value={val}>{val}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 7 }}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="中項目"
                  value={localFilters.category_medium || ''}
                  onChange={(e) => handleChange('category_medium', e.target.value)}
                  disabled={!localFilters.category_large}
                  helperText={!localFilters.category_large ? '先に大項目を選択してください' : ''}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '13px' },
                    '& .MuiOutlinedInput-input': { fontSize: '13px' },
                  }}
                >
                  <MenuItem value="">すべて</MenuItem>
                  {getMediumOptions(localFilters.category_large || '').map((val) => (
                    <MenuItem key={val} value={val}>{val}</MenuItem>
                  ))}
                  {!!localFilters.category_medium && !getMediumOptions(localFilters.category_large || '').includes(localFilters.category_medium) && (
                    <MenuItem key={localFilters.category_medium} value={localFilters.category_medium}>{localFilters.category_medium} (旧)</MenuItem>
                  )}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="備考"
                  placeholder="備考から検索"
                  value={localFilters.remarks || ''}
                  onChange={(e) => handleChange('remarks', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '13px' },
                    '& .MuiOutlinedInput-input': { fontSize: '13px' },
                  }}
                />
              </Grid>

              {/* 備考の除外検索（指定語句を含む案件を結果から除外） */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="備考（除外）"
                  placeholder="この語句を含む案件を除外"
                  value={localFilters.remarks_exclude || ''}
                  onChange={(e) => handleChange('remarks_exclude', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  helperText="入力した語句を備考に含む案件を結果から除外します"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icons.Block sx={{ fontSize: 18, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '13px' },
                    '& .MuiOutlinedInput-input': { fontSize: '13px' },
                    '& .MuiFormHelperText-root': { fontSize: '11px', mx: 0 },
                  }}
                />
              </Grid>

              {/* 表示切替スイッチ */}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2, fontSize: '13px', color: 'gray' }}>表示設定</Divider>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        color="primary"
                        checked={!!localFilters.show_long_term}
                        onChange={(e) => handleChange('show_long_term', e.target.checked)}
                      />
                    }
                    label={<Typography variant="caption">中長期案件のみ表示</Typography>}
                  />
                </Box>
              </Grid>
            </Grid>
          ) : activeTab === 2 && columns ? (
            <SfaListColumnsEditor value={localColumns} onChange={setLocalColumns} />
          ) : (
            <Box sx={{ pt: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                並び替えの基準となる項目を選択してください
              </Typography>
              <Stack spacing={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="ソート項目"
                  value={localSort.field}
                  onChange={(e) => handleSortFieldChange(e.target.value)}
                  sx={{
                    '& .MuiInputLabel-root': { fontSize: '13px' },
                    '& .MuiOutlinedInput-input': { fontSize: '13px' },
                  }}
                >
                  {sortOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>

                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    並び順
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    value={localSort.order}
                    exclusive
                    onChange={handleSortOrderChange}
                    fullWidth
                    size="small"
                  >
                    <ToggleButton value="asc" sx={{ py: 0.5 }}>
                      <Icons.ArrowUpward sx={{ fontSize: 16, mr: 1 }} />
                      昇順 (小さい順)
                    </ToggleButton>
                    <ToggleButton value="desc" sx={{ py: 0.5 }}>
                      <Icons.ArrowDownward sx={{ fontSize: 16, mr: 1 }} />
                      降順 (大きい順)
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Stack>

            </Box>
          )}
        </Box>

        {/* アクションボタン */}
        <Divider sx={{ flexShrink: 0 }} />
        <Box sx={{
          p: 1,
          display: 'flex',
          justifyContent: showSearchButton ? 'space-between' : 'flex-end',
          alignItems: 'center',
          bgcolor: '#f1f5f9',
          flexShrink: 0,
        }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {showResetButton && (
              <Button
                size="small"
                onClick={handleReset}
                startIcon={<Icons.RestartAlt />}
                sx={STANDARD_TEXT_BUTTON_SX}
              >
                リセット
              </Button>
            )}

            <Fab
              variant="extended"
              size="small"
              onClick={handleOpenSaveDialog}
              sx={STANDARD_FAB_CTA_SX}
            >
              <Icons.Save />
              条件を保存
            </Fab>
          </Box>

          {showSearchButton && (
            <Fab
              variant="extended"
              size="small"
              disableRipple
              onClick={handleSearch}
              sx={STANDARD_FAB_CTA_SX}
            >
              <Icons.Search />
              検索を実行
            </Fab>
          )}
        </Box>
      </Box>

      {/* 保存ダイアログ */}
      <Dialog open={isSaveDialogOpen} onClose={handleCloseSaveDialog}>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 'bold' }}>検索条件を保存</DialogTitle>
        <DialogContent sx={{ minWidth: '360px' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {columns ? '現在のフィルター・ソート・表示列を保存します。' : '現在のフィルターとソート設定を保存します。'}
            保存した条件は案件条件リストに並びます。
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="条件名"
            type="text"
            fullWidth
            variant="outlined"
            size="small"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            placeholder="例：未着工の重要案件"
            sx={{
              '& .MuiInputLabel-root': { fontSize: '13px' },
              '& .MuiOutlinedInput-input': { fontSize: '13px' },
            }}
          />

          {/* 共有区分（個人／指名共有）。共有先は閲覧と複製のみ、編集・削除は作成者のみ */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 0.5 }}>
            共有区分
          </Typography>
          <ToggleButtonGroup
            color="primary"
            value={scope}
            exclusive
            size="small"
            fullWidth
            onChange={(_e, v: SavedFilterScope | null) => { if (v) setScope(v); }}
          >
            <ToggleButton value="personal" sx={{ py: 0.5, fontSize: '12px' }}>
              <Icons.Person sx={{ fontSize: 16, mr: 0.5 }} />
              個人
            </ToggleButton>
            <ToggleButton value="shared" sx={{ py: 0.5, fontSize: '12px' }}>
              <Icons.Groups sx={{ fontSize: 16, mr: 0.5 }} />
              共有
            </ToggleButton>
          </ToggleButtonGroup>
          {scope === 'shared' && (
            <>
              <Autocomplete
                multiple
                size="small"
                sx={{ mt: 1.5 }}
                options={staffOptions}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(a, b) => a.email === b.email}
                value={sharedWith.map((e) => staffByEmail.get(e) ?? { email: e, name: e })}
                onChange={(_e, v) => setSharedWith(v.map((o) => o.email))}
                noOptionsText="該当するスタッフがいません"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="共有メンバー"
                    placeholder="氏名で検索"
                    sx={{
                      '& .MuiInputLabel-root': { fontSize: '13px' },
                      '& .MuiOutlinedInput-input': { fontSize: '13px' },
                    }}
                  />
                )}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                共有先は閲覧と複製のみできます。編集・削除は作成者のみです。
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseSaveDialog} sx={STANDARD_TEXT_BUTTON_SX}>キャンセル</Button>
          <Button
            onClick={handleSaveFilter}
            disabled={isSaving || !filterName.trim()}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Icons.Save />}
            sx={STANDARD_CTA_BUTTON_SX}
          >
            {isSaving ? '保存中...' : '保存する'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SalesProjectSearchFilters;
